import { useCallback, useEffect, useState } from 'react'
import { useFormikContext } from 'formik'
import { useBulkActionContext } from '../../../BulkActionContext'
import {
  BulkReassignFormModel,
  ErrorSummary,
  ValidationCallback,
} from '../typings'
import { BulkReassignDTO } from '../../../typings/BulkReassign'
import { formatErrors } from '../functions'
import { produce } from 'immer'
import { useSelector } from 'react-redux'
import { selectAllSalesTeamUsers } from '@user/store/salesTeam'

export const useValidateRows = (validateRows: ValidationCallback) => {
  const {
    values: { salesTeamId, assigneeId, removeServices },
  } = useFormikContext<BulkReassignFormModel>()

  const {
    selectedRows,
    dispatch,
    state: { isValidating },
  } = useBulkActionContext()
  const allUsers = useSelector(selectAllSalesTeamUsers)

  const updateState = useCallback(
    (dto?: BulkReassignDTO) => {
      const {
        errors,
        hasAlreadyClosed,
        hasWrongBranch,
        hasLocationMatches,
        hasServices,
        hasOpen,
        hasServicesCount,
      } = formatErrors(dto, removeServices)

      dispatch({ type: 'setErrors', payload: errors })
      setErrorSummary((prev) =>
        produce(prev, (draft) => {
          draft.count = errors.size
          draft.hasAlreadyClosed = hasAlreadyClosed
          draft.hasWrongBranch = hasWrongBranch
          draft.hasLocationMatches = hasLocationMatches
          draft.hasServices = hasServices
          draft.hasOpen = hasOpen
          draft.hasServicesCount = hasServicesCount ?? 0
        }),
      )
    },
    [dispatch, removeServices],
  )

  const [errorSummary, setErrorSummary] = useState<ErrorSummary>({
    count: 0,
    hasAlreadyClosed: false,
    hasWrongBranch: false,
    hasLocationMatches: false,
    hasServices: false,
    hasOpen: false,
    hasServicesCount: 0,
  })

  const doValidation = useCallback(
    async (mounted: boolean) => {
      dispatch({ type: 'startValidating' })
      const assignee =
        assigneeId && assigneeId !== 'unassigned'
          ? allUsers.find((x) => x.userId === assigneeId)
          : null
      const dto = await validateRows({
        ids: selectedRows.map((x) => Number(x)),
        salesTeamId: Number(salesTeamId),
        assigneeId: assigneeId === 'unassigned' ? null : assigneeId,
        removeServices: removeServices,
        activeOwner: assigneeId === 'unassigned' || assignee?.isActive,
      })
      if (mounted) updateState(dto)
    },
    [
      dispatch,
      assigneeId,
      allUsers,
      validateRows,
      selectedRows,
      salesTeamId,
      removeServices,
      updateState,
    ],
  )

  useEffect(() => {
    let mounted = true

    const shouldValidate = salesTeamId !== '' && assigneeId !== ''
    if (shouldValidate) doValidation(mounted)
    else updateState()

    return () => {
      mounted = false
    }
  }, [assigneeId, salesTeamId, updateState, doValidation])

  return {
    errorSummary,
    loading: isValidating,
  }
}
